<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import { useSession } from '@/auth/session';
import { visibleModulesFor } from '@/data/modules';
import type { BomLedgerSummary } from '@/types/bom-ledger';
import type { ModuleCatalogItem } from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';

/* 和侧栏、指挥台用同一把尺子：管理员专属模块不出现在模块中心的卡片里 */
const session = useSession();
const { canManageUsers } = session;
const visibleModules = computed(() => visibleModulesFor(session.canManageUsers.value));

const catalog = ref<Record<string, ModuleCatalogItem>>({});
const error = ref('');
/* 量产 BOM 的数不在通用模块目录里（那边按单表计数），走自己的接口；读不到就不显示数字 */
const bom = ref<BomLedgerSummary | null>(null);

onMounted(async () => {
  void eqsApi.bomLedgerSummary().then((value) => { bom.value = value; }).catch(() => undefined);
  try {
    const payload = await eqsApi.moduleCatalog();
    catalog.value = Object.fromEntries(payload.map((item) => [item.key, item]));
  } catch (caught) {
    error.value = errorMessage(caught);
  }
});

/* 目录接口回的 availability 是英文码：只翻认得的两种，认不出的照实标「待确认类型」，不自己编分类 */
function availabilityLabel(value: string | undefined | null): string {
  if (!value) return '—';
  if (value === 'available') return '可用';
  if (value === 'partial') return '部分可用';
  return '待确认类型';
}
</script>

<template>
  <section class="module-grid">
    <RouterLink v-for="(module, index) in visibleModules" :key="module.key" :to="module.route" class="module-card">
      <header><b>{{ String(index + 1).padStart(2, '0') }} / {{ module.code }}</b><span>进入模块 →</span></header>
      <h2>{{ module.name }}</h2>
      <p>{{ module.caption }}</p>
      <dl v-if="catalog[module.module]">
        <div><dt>记录</dt><dd>{{ catalog[module.module].count }}</dd></div>
        <div><dt>待办</dt><dd>{{ catalog[module.module].openCount }}</dd></div>
        <div :class="{ risk: catalog[module.module].health === 'risk' }"><dt>状态</dt><dd>{{ catalog[module.module].health === 'risk' ? '异常' : catalog[module.module].health === 'warning' ? '关注' : '正常' }}</dd></div>
      </dl>
      <small v-if="catalog[module.module]">更新 {{ formatDate(catalog[module.module].refreshedAt) }} · {{ availabilityLabel(catalog[module.module].availability) }}</small>
      <small v-else-if="error" class="module-error" :title="error">模块目录接口不可用 · 进入查看详情</small>
      <small v-else>正在读取模块统计…</small>
    </RouterLink>

    <!--
      量产 BOM（1030）：移交量产后的 BOM 入口。不进 modules 注册表——它不是一张通用记录表，
      和下面审批流那张卡同一个理由。所有人可见：等你确认同步的人就是从这里进去的。
    -->
    <RouterLink to="/modules/bom" class="module-card">
      <header><b>{{ String(visibleModules.length + 1).padStart(2, '0') }} / BOM</b><span>进入模块 →</span></header>
      <h2>量产 BOM</h2>
      <p>移交量产后的换版、同步与确认</p>
      <dl v-if="bom">
        <div><dt>产品</dt><dd>{{ bom.products }}</dd></div>
        <div><dt>待确认</dt><dd>{{ bom.pending }}</dd></div>
        <div :class="{ risk: bom.mine > 0 }"><dt>等我确认</dt><dd>{{ bom.mine }}</dd></div>
      </dl>
    </RouterLink>

    <!--
      审批流不走通用记录工作台（它编辑的是流程和步骤，不是一张记录表），
      所以单独一张卡，不塞进 modules 注册表里去破坏那边的约定。
      0901 起和 ORG / CFG 一样只给管理员：页面里本来就只有管理员能改，
      普通人进来是一屏只读的流程配置——看得到用不了，不如不摆。

      0916：原「系统设置」撤掉，它里面真正有用的两块（邮件入库试点结果、
      字典只读查看）并到这张卡后面，所以改叫「审批流与字典」。
    -->
    <RouterLink v-if="canManageUsers" to="/modules/approvals" class="module-card">
      <header><b>{{ String(visibleModules.length + 2).padStart(2, '0') }} / WF</b><span>进入模块 →</span></header>
      <h2>审批流与字典</h2>
      <p>维护审批路径并挂到业务节点；下方是字典取值和邮件入库试点</p>
      <small>流程与步骤可编辑 · 改动对已挂节点立即生效</small>
    </RouterLink>
  </section>
</template>
